import React, { useMemo, useState } from 'react';
import { Button, Dropdown, Popover, Tooltip } from 'antd';

import ItemLookupSelect from './ItemLookupSelect';
import TagSelect from '../tags/TagSelect';
import { UserPickerRow } from '../users/UserPickerSelect';
import useCollaborators from '../../hooks/useCollaborators';
import useMe from '../../hooks/useMe';
import useQueryLookupTitles from '../../hooks/useQueryLookupTitles';
import { CHEVRON_DOWN, CLOSE, USER_ACCOUNT } from '../icons';
import { booleanLabels } from '../../utils/elements';
import { bool as makeBool, me as makeMe, str as makeStr, isElementIdSegment } from '../../utils/kql';
import { conditionValueLabel, displayValue, elementGroupSegment, elementIdKeys, fieldKindOf, fieldsHaveAccountLink, findField, isElementGroupSegment, isMeValue, normalizeFacetAliasText, pathKey } from '../../utils/kqlBuilder';
import { clearConditionsOnPath, readConditionOnPath } from '../../utils/filterValues';
import { quickFilterFields, quickFilterInScope, quickFilterLabel } from '../../utils/facets';
import { ME_VALUE, userDisplayName } from '../../utils/userElements';
import { normalizeId } from '../../utils/ids';

/**
 * Quick-filter chips — the question the list is usually asked, already on the
 * toolbar.
 *
 * "+ Filter" opens a panel that can express anything; a quick filter is the
 * opposite trade. A collection about visits is filtered by PERSON all day
 * long, and a manager should not open a panel, add a row, find the field and
 * pick a value to ask it. So the facets (utils/facets.js) and the elements an
 * editor promoted (`options.quickFilter` on a choice, a yes/no or a lookup)
 * each get one chip on the filter row: click, pick, done.
 *
 * NOTHING NEW IS DECIDED HERE. A pick goes through the page's own
 * `applyValueFilter` — the same AND / dedupe / replace semantics every "filter
 * by this value" affordance uses (utils/filterValues.js), so picking a second
 * person REPLACES the first rather than selecting nobody. An active chip shows
 * what is picked and carries the ✕ that clears it, which removes every
 * top-level condition on that path: the chip owns the whole facet, not one of
 * two conditions a reader cannot tell apart.
 *
 * A chip whose condition the query holds in a shape the chip did not write
 * (nested under an `or`, a `>` nobody's menu offers) renders INACTIVE rather
 * than claiming a filter it cannot clear — the chips under the toolbar still
 * show it, and the builder still edits it.
 */

/**
 * How a chip SPELLS its element (element ids step 3, KPR-238 P4): a pick
 * writes the element itself — its id where one element of the chip's type
 * answers to the name (`#k7f3q2xa`), or the element group across them where
 * several cards each have their own ("any of these cards", `#a = x or #b =
 * x`), never the name — and the chip reads, replaces and clears a condition
 * written in any of those spellings, or by the name (text saved before).
 * `{ path, forceElement, aliases }`; a facet or a field with no id keeps its
 * own path.
 */
export function chipSpelling(field) {
	// EVERY card's element of the chip's name — what the name read before
	// ids, and what the server would list as its candidates — never only the
	// definitions of the field's own type, which would drop the other cards'
	// items without anyone choosing that (the P4 review's R-QC1).
	const idKeys = [...new Set([
		...elementIdKeys(field),
		...((field && field.definitions) || []).filter(def => def && def.id).map(def => pathKey([`#${def.id}`])),
	])];
	const own = [...(field.aliases || []), ...idKeys];
	if (!idKeys.length || field.facet) return { path: [...field.path], forceElement: !!field.forceElement, aliases: own };
	const segment = idKeys.length === 1 ? idKeys[0] : elementGroupSegment(idKeys.map(key => key.slice(1)));
	return {
		path: [segment],
		forceElement: false,
		aliases: [...new Set([...own, segment, pathKey(field.path, !!field.forceElement)])],
	};
}

/** The one condition a pick applies, or null when the value means "any". */
const conditionFor = (field, raw) => {
	if (raw === undefined || raw === null || raw === '') return null;
	const kind = fieldKindOf(field);
	if (kind === 'boolean') return { cmp: 'eq', value: makeBool(raw === true || raw === 'true') };
	// "Me" is the grammar's keyword — the caller's account on a user
	// element, the caller's linked record on a lookup or a facet.
	if ((kind === 'user' || kind === 'lookup') && isMeValue(raw)) return { cmp: 'eq', value: makeMe() };
	return { cmp: 'eq', value: makeStr(String(raw)) };
};

/** The "Me" row's label: the account glyph, the word, and who that is on hover. */
const MeMenuLabel = ({ tooltip }) => (
	<Tooltip title={tooltip} placement="right">
		<span className="quick-filter-chip__me">
			<span className="user-picker__me-icon" aria-hidden="true"><USER_ACCOUNT /></span>
			Me
		</span>
	</Tooltip>
);

/** The menu a choice or a yes/no offers: its values, headed by "Any". */
const menuItemsFor = field => {
	if (fieldKindOf(field) === 'boolean') {
		const { trueLabel, falseLabel } = booleanLabels(field);
		return [
			{ key: '', label: `Any ${quickFilterLabel(field).toLowerCase()}` },
			{ key: 'true', label: trueLabel },
			{ key: 'false', label: falseLabel },
		];
	}
	const choices = Array.isArray(field.options?.choices) ? field.options.choices : [];
	return [
		{ key: '', label: `Any ${quickFilterLabel(field).toLowerCase()}` },
		...choices.map(choice => {
			const value = choice !== null && typeof choice === 'object' ? choice.value : choice;
			const label = choice !== null && typeof choice === 'object' ? (choice.label ?? choice.value) : choice;
			return { key: String(value), label: String(label) };
		}),
	];
};

/** One chip: a button that opens its picker, plus the ✕ that clears it. */
function QuickFilterChip({ field: ownField, fields = [], query, collectionId, onApply, onChange, titleFor, refLabelFor, collectionHasLink = false, outOfScope = false }) {
	const [open, setOpen] = useState(false);
	const spelling = chipSpelling(ownField);
	// A chip that asks several cards' elements at once offers what any of
	// them stores (the group's field merges their choices).
	const field = isElementGroupSegment(spelling.path[0])
		? { ...ownField, options: { ...ownField.options, choices: findField(fields, spelling.path).options?.choices || ownField.options?.choices } }
		: ownField;

	const label = quickFilterLabel(field);
	const kind = fieldKindOf(field);
	// A user chip's menu is the roster: "Me" first, then the collaborators.
	// The tooltip on "Me" says who that is, through the collection's own
	// answer (GET …/me) — the item form's shortcut reads the same source.
	const isUserChip = kind === 'user';
	const { users: collaborators } = useCollaborators(isUserChip ? collectionId : null);
	const me = useMe(isUserChip || (kind === 'lookup' && collectionHasLink) ? collectionId : null);
	const meName = userDisplayName(me.user);
	const meTooltip = isUserChip
		? `Items assigned to you${meName ? ` (${meName})` : ''}`
		: `Items pointing at your own record here${meName ? ` (${meName})` : ''}`;
	// The element's ids are this chip's path too: a chart's door names it
	// `#k7f3q2xa` (KPR-168; review EK-W2), and a pick writes it (P4).
	const idKeys = spelling.aliases;
	const active = readConditionOnPath(query, field.path, field.forceElement, field.facet, idKeys);
	// Only the comparisons a picker can write read back as a picked value; a
	// hand-authored `is not` is a filter the chip shows as set but does not
	// pretend to have chosen.
	const picked = active && (active.cmp === 'eq' || active.cmp === 'in')
		? displayValue(active.value, raw => titleFor(raw) ?? conditionValueLabel(field, raw), refLabelFor)
		: null;
	const text = picked ? `${label}: ${picked}` : label;

	const apply = raw => {
		setOpen(false);
		const condition = conditionFor(field, raw);
		if (!condition) {
			onChange(clearConditionsOnPath(query, field.path, field.forceElement, field.facet, idKeys));
			return;
		}
		// A condition the chip read by the element's ID (a chart's door wrote
		// `#k7f3q2xa = …`) — or as an element group — is written back that
		// way, never turned into the name (the re-review's EK2-W1): the id
		// names the one element the door meant, whichever card spells the
		// name first. Anything else is written as the chip spells its element
		// (`chipSpelling`: an id, or the group — never the name, P4).
		const byId = active && Array.isArray(active.path) && active.path.length === 1
			&& (isElementIdSegment(active.path[0]) || isElementGroupSegment(active.path[0])) ? active.path : null;
		onApply({
			path: byId ? [...byId] : [...spelling.path],
			forceElement: byId ? false : spelling.forceElement,
			facet: !!field.facet,
			// The chip owns the whole path — its ✕ clears every condition on
			// it and it reads back only the first — so a pick must TAKE THE
			// PLACE of what is there, not just of a condition with the same
			// operator. Otherwise picking Carol over a builder-authored
			// `is any of (Alice, Bob)` leaves both, and the chip goes on
			// naming Alice and Bob.
			replacePath: true,
			// A facet's key spelling (text written before F3) is this path too.
			// …and so are the element's ids, its group, and its name.
			aliases: [...new Set([...idKeys, pathKey(field.path, !!field.forceElement)])],
			label,
			valueLabel: String(raw),
			conditions: [condition],
		});
	};

	// No onClick of its own: the Dropdown/Popover around it owns the trigger,
	// and a second toggle here would cancel antd's out in the same batch.
	// A chip whose element has left the cards in view but whose condition is
	// still applied stays on the row, DIMMED — with its value and its ✕
	// intact. Dropping it would silently remove a condition from the user's
	// query, or leave one applied with nothing on screen that can clear it.
	const scopeNote = outOfScope
		? ((field.cardLabels || []).length
			? `Only on ${[...new Set(field.cardLabels)].join(', ')} — not on the cards in view`
			: 'Not on the cards in view')
		: null;
	const bareTrigger = (
		<button
			type="button"
			className={`view-chip quick-filter-chip${active ? ' view-chip--active' : ''}${outOfScope ? ' quick-filter-chip--out-of-scope' : ''}`}
			aria-label={picked ? `${label}: ${picked}` : `Filter by ${label}`}
			aria-expanded={open}
		>
			<span className="view-chip__name">{text}</span>
			<CHEVRON_DOWN className="quick-filter-chip__caret" aria-hidden="true" />
		</button>
	);
	const trigger = scopeNote ? <Tooltip title={scopeNote} placement="top">{bareTrigger}</Tooltip> : bareTrigger;

	// A lookup (and every facet) picks an ITEM, so it opens the same control
	// the builder's lookup row uses rather than a menu of everything. The
	// Tags chip picks a TAG from the tree — the builder's tag picker.
	const picksAnItem = kind === 'lookup';
	const picksATag = kind === 'tag';
	const lookupCardId = field.options?.lookupCardId || field.lookupCardId;

	const userMenuItems = isUserChip ? [
		{ key: '', label: `Any ${label.toLowerCase()}` },
		{ key: ME_VALUE, label: <MeMenuLabel tooltip={meTooltip} /> },
		...(collaborators.length ? [{ type: 'divider' }] : []),
		...collaborators.map(user => ({ key: normalizeId(user._id), label: <UserPickerRow user={user} /> })),
	] : null;

	const tagOptions = field.options || {};
	const picker = picksATag ? (
		<Popover
			open={open}
			onOpenChange={setOpen}
			trigger="click"
			placement="bottomLeft"
			destroyTooltipOnHide
			overlayClassName="quick-filter-chip__popover"
			content={(
				<div className="quick-filter-chip__picker">
					<TagSelect
						className="quick-filter-chip__select"
						value={undefined}
						collectionTags={tagOptions.tags || []}
						privateTags={tagOptions.mode === 'collection' ? null : (tagOptions.privateTags || [])}
						itemTitles={tagOptions.itemTitles}
						aria-label={label}
						placeholder="Pick a tag"
						autoFocus
						onChange={next => { if (next) apply(next); }}
					/>
				</div>
			)}
		>
			{trigger}
		</Popover>
	) : picksAnItem ? (
		<Popover
			open={open}
			onOpenChange={setOpen}
			trigger="click"
			placement="bottomLeft"
			destroyTooltipOnHide
			overlayClassName="quick-filter-chip__popover"
			content={(
				<div className="quick-filter-chip__picker">
					{collectionHasLink ? (
						<Tooltip title={meTooltip} placement="top">
							<Button
								size="small"
								className="quick-filter-chip__me-button"
								icon={<USER_ACCOUNT />}
								aria-label={`${label}: me`}
								onClick={() => apply(ME_VALUE)}
							>
								Me
							</Button>
						</Tooltip>
					) : null}
					{lookupCardId && collectionId ? (
						<ItemLookupSelect
							className="quick-filter-chip__select"
							collectionId={collectionId}
							cardId={lookupCardId}
							value={undefined}
							ariaLabel={label}
							placeholder={`Pick ${label.toLowerCase()}`}
							onChange={next => apply(next)}
						/>
					) : (
						<p className="quick-filter-chip__empty">Nothing to pick from here.</p>
					)}
				</div>
			)}
		>
			{trigger}
		</Popover>
	) : (
		<Dropdown
			open={open}
			onOpenChange={setOpen}
			trigger={['click']}
			placement="bottomLeft"
			overlayClassName="quick-filter-chip__menu"
			menu={{ items: userMenuItems || menuItemsFor(field), onClick: ({ key }) => apply(key) }}
		>
			{trigger}
		</Dropdown>
	);

	return (
		<span className="quick-filter">
			{picker}
			{active ? (
				<button
					type="button"
					className="quick-filter__clear"
					aria-label={`Clear ${label} filter`}
					onClick={() => onChange(clearConditionsOnPath(query, field.path, field.forceElement, field.facet, idKeys))}
				>
					<CLOSE />
				</button>
			) : null}
		</span>
	);
}

export default function QuickFilterChips({
	fields = [],
	query: rawQuery = '',
	collectionId = null,
	onApply,
	onChange,
	className = 'quick-filters',
	// Which cards the active filter can possibly return (utils/filterScope.js).
	// null = unrestricted; an EMPTY Set is a real answer and narrows to the
	// pinned and the active alone.
	scopeCardIds = null,
	// The collection's author control (collection.quickFilters).
	pinned = [],
	hidden = [],
	// How many chips the row shows before the rest go behind "More filters".
	limit = null,
}) {
	// A facet named by its card's KEY — text written before filters named
	// cards by id (F3) — is the same chip: it is read, cleared and replaced on
	// the query with those aliases renamed to the facet's id, so a pick never
	// leaves an `@people` condition beside the `@<id>` one it replaces.
	const query = useMemo(() => normalizeFacetAliasText(rawQuery, fields), [rawQuery, fields]);

	// A chip the reader pulled out of the overflow menu. Session-local and
	// deliberately not persisted: it is "show me that one now", not a pin.
	const [revealed, setRevealed] = useState(() => new Set());

	// EVERY chip the vocabulary offers, before ANY narrowing. The active read
	// has to run over this list: a chip whose element just left scope still
	// holds its condition, and the row is the only place that condition can be
	// cleared from.
	const allChips = useMemo(() => quickFilterFields(fields), [fields]);
	const activeKeys = useMemo(() => new Set(
		allChips
			.filter(field => readConditionOnPath(query, field.path, field.forceElement, field.facet, chipSpelling(field).aliases))
			.map(field => field.key)
	), [allChips, query]);

	const { shown, overflow } = useMemo(() => {
		const result = quickFilterFields(fields, { scopeCardIds, activeKeys, pinned, hidden, limit });
		const rows = Array.isArray(result) ? { shown: result, overflow: [] } : result;
		if (!revealed.size) return rows;
		// A revealed chip joins the row in its own order, rather than jumping
		// to the front: the reader picked it out of a list they were reading.
		const pulled = new Set(rows.overflow.filter(field => revealed.has(field.key)).map(field => field.key));
		if (!pulled.size) return rows;
		const ordered = [...rows.shown, ...rows.overflow];
		return {
			shown: ordered.filter(field => !rows.overflow.includes(field) || pulled.has(field.key)),
			overflow: rows.overflow.filter(field => !pulled.has(field.key)),
		};
	}, [fields, scopeCardIds, activeKeys, pinned, hidden, limit, revealed]);

	// A picked item is stored as an id; the chip has to say the person's name,
	// through the same batch resolve the applied chips use.
	//
	// THE FULL FIELD LIST, NOT THE NARROWED ONE, goes to both of these. Hand
	// them the scoped list and an out-of-scope active chip renders a raw
	// ObjectId instead of a person's name, and the "Me" affordance flips off
	// mid-session — the narrowing is a presentation question and these two
	// answer a different one.
	const { titleFor } = useQueryLookupTitles(query, fields);
	// A `{{reference}}` reads as the element's own label — by id
	// (`{{#k7f3q2xa}}`) as by name — never the id or the slug the query carries.
	const refLabelFor = useMemo(() => name => findField(fields, [name]).label, [fields]);
	// Lookup and facet chips offer "Me" (the reader's own record) only where
	// a card of THIS collection carries an account link — read off the
	// vocabulary the page built from its member cards, not the app-wide cache.
	const collectionHasLink = useMemo(() => !!collectionId && fieldsHaveAccountLink(fields), [collectionId, fields]);

	if ((!shown.length && !overflow.length) || typeof onApply !== 'function' || typeof onChange !== 'function') return null;

	return (
		<div className={className}>
			{shown.map(field => (
				<QuickFilterChip
					key={field.key}
					field={field}
					fields={fields}
					query={query}
					collectionId={collectionId}
					onApply={onApply}
					onChange={onChange}
					titleFor={titleFor}
					refLabelFor={refLabelFor}
					collectionHasLink={collectionHasLink}
					outOfScope={!quickFilterInScope(field, scopeCardIds)}
				/>
			))}
			{overflow.length ? (
				<Dropdown
					trigger={['click']}
					placement="bottomLeft"
					overlayClassName="quick-filter-chip__menu"
					menu={{
						items: overflow.map(field => ({
							key: field.key,
							label: quickFilterLabel(field),
						})),
						onClick: ({ key }) => setRevealed(prev => new Set([...prev, key])),
					}}
				>
					<button
						type="button"
						className="view-chip quick-filter-chip quick-filter-chip--more"
						aria-label={`More filters (${overflow.length})`}
					>
						<span className="view-chip__name">{`More filters (${overflow.length})`}</span>
						<CHEVRON_DOWN className="quick-filter-chip__caret" aria-hidden="true" />
					</button>
				</Dropdown>
			) : null}
		</div>
	);
}
